import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistorique } from '../lib/historique'
import { useAuth, peutEcrire } from '../lib/auth'
import { surChangementFile } from '../lib/sync'
import { jourCalendrier } from '../lib/format'
import {
  classeStatut,
  codeSuivi,
  comptePhotos,
  enRetard,
  equipe,
  nomAssigne,
  estFerme,
  LIBELLE_STATUT,
  rafraichirSuivis,
  suivisLocaux,
  TYPES
} from '../lib/suivis'
import type { Chantier, Profil, SuiviLocal, TypeSuivi } from '../lib/types'
import { FicheSuivi } from './FicheSuivi'

type Filtre = 'ouverts' | 'moi' | 'fermes' | 'tous'

const FILTRES: { cle: Filtre; libelle: string }[] = [
  { cle: 'ouverts', libelle: 'Ouverts' },
  { cle: 'moi', libelle: 'À moi' },
  { cle: 'fermes', libelle: 'Fermés' },
  { cle: 'tous', libelle: 'Tous' }
]

/**
 * Onglet Suivis : trois listes en sous-onglets (décision d'ergonomie no 4).
 * Le bouton « + » crée un suivi du type de la sous-liste affichée (no 3).
 */
export function Suivis({
  chantier,
  depuisPhoto,
  depuisPhotoTraitee,
  voirSurPlan
}: {
  chantier: Chantier
  /** Photo à partir de laquelle créer une déficience (action sur la fiche photo). */
  depuisPhoto: string | null
  depuisPhotoTraitee: () => void
  voirSurPlan?: (s: SuiviLocal) => void
}) {
  const { profil } = useAuth()
  const [type, setType] = useState<TypeSuivi>('deficience')
  const [filtre, setFiltre] = useState<Filtre>('ouverts')
  const [suivis, setSuivis] = useState<SuiviLocal[]>([])
  const [photos, setPhotos] = useState<Map<string, number>>(new Map())
  const [noms, setNoms] = useState<Map<string, string>>(new Map())
  const [ouvert, setOuvert] = useState<string | null>(null)
  const [creation, setCreation] = useState<{ type: TypeSuivi; photo?: string } | null>(null)
  const [chargement, setChargement] = useState(true)
  useHistorique(ouvert, setOuvert)
  useHistorique(creation, setCreation, (c) => c !== null)
  const [erreur, setErreur] = useState<string | null>(null)
  const ecrivable = peutEcrire(profil, chantier.statut)

  const lireLocal = useCallback(async () => {
    const l = await suivisLocaux(chantier.id)
    setSuivis(l)
    setPhotos(await comptePhotos(l.map((s) => s.id)))
  }, [chantier.id])

  const charger = useCallback(async () => {
    setErreur(null)
    try {
      await lireLocal()
      await rafraichirSuivis(chantier.id)
    } catch (e) {
      setErreur(e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e))
    }
    await lireLocal()
    setChargement(false)
  }, [chantier.id, lireLocal])

  useEffect(() => {
    void charger()
    void equipe().then((m: Pick<Profil, 'id' | 'nom'>[]) => setNoms(new Map(m.map((p) => [p.id, p.nom]))))
  }, [charger])

  useEffect(() => surChangementFile(() => void lireLocal()), [lireLocal])

  // Créer une déficience à partir d'une photo : ouvre directement la fiche.
  useEffect(() => {
    if (!depuisPhoto) return
    setType('deficience')
    setCreation({ type: 'deficience', photo: depuisPhoto })
    depuisPhotoTraitee()
  }, [depuisPhoto, depuisPhotoTraitee])

  const parType = useMemo(() => {
    const m = new Map<TypeSuivi, SuiviLocal[]>()
    for (const t of TYPES) m.set(t.cle, [])
    for (const s of suivis) m.get(s.type)?.push(s)
    return m
  }, [suivis])

  const liste = useMemo(() => {
    const l = (parType.get(type) ?? []).filter((s) => {
      if (filtre === 'ouverts') return !estFerme(s)
      if (filtre === 'fermes') return estFerme(s)
      if (filtre === 'moi') return !estFerme(s) && s.assigne_a === profil?.id
      return true
    })
    return l.sort((a, b) => {
      const fa = estFerme(a)
      const fb = estFerme(b)
      if (fa !== fb) return fa ? 1 : -1
      if (fa && fb) return (b.ferme_le ?? '').localeCompare(a.ferme_le ?? '')
      const ra = enRetard(a)
      const rb = enRetard(b)
      if (ra !== rb) return ra ? -1 : 1
      // Déficiences réglées (à valider) en tête : c'est l'action en attente.
      if ((a.statut === 'reglee') !== (b.statut === 'reglee')) return a.statut === 'reglee' ? -1 : 1
      if (a.echeance && b.echeance && a.echeance !== b.echeance) return a.echeance.localeCompare(b.echeance)
      if (!!a.echeance !== !!b.echeance) return a.echeance ? -1 : 1
      return (b.numero ?? 1e9) - (a.numero ?? 1e9)
    })
  }, [parType, type, filtre, profil?.id])

  const fermerFiche = useCallback(() => {
    setOuvert(null)
    setCreation(null)
    void lireLocal()
  }, [lireLocal])

  const info = TYPES.find((t) => t.cle === type)!

  return (
    <>
      {(ouvert || creation) && (
        <FicheSuivi
          key={ouvert ?? 'nouveau'}
          chantier={chantier}
          suiviId={ouvert ?? undefined}
          nouveau={creation ?? undefined}
          fermer={fermerFiche}
          voirSurPlan={
            voirSurPlan
              ? (s) => {
                  setOuvert(null)
                  voirSurPlan(s)
                }
              : undefined
          }
        />
      )}

      <div className="sous-onglets" role="tablist" aria-label="Type de suivi">
        {TYPES.map((t) => {
          const ouverts = (parType.get(t.cle) ?? []).filter((s) => !estFerme(s)).length
          const retard = (parType.get(t.cle) ?? []).some(enRetard)
          return (
            <button key={t.cle} role="tab" aria-selected={type === t.cle} onClick={() => setType(t.cle)}>
              {t.libelle}
              <span className={`compte ${retard ? 'alerte' : ''}`}>{ouverts}</span>
            </button>
          )
        })}
      </div>

      <div className="jetons" style={{ marginTop: 0, marginBottom: 12 }}>
        {FILTRES.map((f) => (
          <button key={f.cle} className={`jeton ${filtre === f.cle ? 'chaud' : ''}`} onClick={() => setFiltre(f.cle)}>
            {f.libelle}
          </button>
        ))}
      </div>

      {erreur && (
        <p className="avis erreur" style={{ marginBottom: 12 }}>
          {erreur}
        </p>
      )}

      {chargement && suivis.length === 0 && <p className="vide">Chargement…</p>}

      {!chargement && liste.length === 0 && (
        <p className="vide">
          {filtre === 'ouverts'
            ? `Aucune ${info.singulier} ouverte.`
            : filtre === 'moi'
              ? `Aucune ${info.singulier} ouverte ne t'est assignée.`
              : filtre === 'fermes'
                ? `Aucune ${info.singulier} fermée.`
                : `Aucune ${info.singulier} sur ce chantier.`}
          {ecrivable && (
            <>
              <br />
              Le bouton « + » crée une {info.singulier}.
            </>
          )}
        </p>
      )}

      <div className="liste">
        {liste.map((s) => {
          const retard = enRetard(s)
          const nbPhotos = photos.get(s.id) ?? 0
          return (
            <button
              key={s.id}
              className={`carte ${retard ? 'bande' : estFerme(s) ? 'bande-fermee' : 'bande-nulle'}`}
              onClick={() => setOuvert(s.id)}
            >
              <div className="entre">
                <div style={{ minWidth: 0 }}>
                  <p className="code">{codeSuivi(s)}</p>
                  <p className="titre">{s.titre}</p>
                </div>
                <span className={`pastille ${classeStatut(s)}`}>{LIBELLE_STATUT[s.statut] ?? s.statut}</span>
              </div>
              <p className="meta">
                <span>{nomAssigne(s, noms) ?? 'Non assignée'}</span>
                {s.echeance && (
                  <>
                    <span className="pt" />
                    <span className={retard ? 'retard' : ''}>
                      {retard ? 'En retard · ' : 'Échéance '}
                      {jourCalendrier(s.echeance)}
                    </span>
                  </>
                )}
                {s.emplacement && (
                  <>
                    <span className="pt" />
                    <span>{s.emplacement}</span>
                  </>
                )}
                {s.epingle_libelle && (
                  <>
                    <span className="pt" />
                    <span>Plan {s.epingle_libelle.split(' · ')[0]}</span>
                  </>
                )}
                {nbPhotos > 0 && (
                  <>
                    <span className="pt" />
                    <span>
                      {nbPhotos} photo{nbPhotos > 1 ? 's' : ''}
                    </span>
                  </>
                )}
                {s.etat !== 'ok' && (
                  <>
                    <span className="pt" />
                    <span>En file</span>
                  </>
                )}
              </p>
            </button>
          )
        })}
      </div>

      {ecrivable && !ouvert && !creation && (
        <button className="fab" onClick={() => setCreation({ type })} aria-label={`Nouvelle ${info.singulier}`}>
          +
        </button>
      )}
    </>
  )
}
